import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { DEFAULT_SIMULATION_DATE } from '../../../config/constants';
import type { Environment } from './EnvironmentSelector';
import { DataTypeTabs, type DataType } from './DataTypeTabs';
import { PlaceholderSection } from './PlaceholderSection';
import { LocationsDownloadSection } from './LocationsDownloadSection';
import { RoutesDownloadSection } from './RoutesDownloadSection';
import { RoutesTransferSection } from './RoutesTransferSection';
import { FleetsDownloadSection } from './FleetsDownloadSection';
import { ServiceInformationDownloadSection } from './ServiceInformationDownloadSection';
import { ServicesDownloadSection } from './ServicesDownloadSection';
import { RidesDownloadSection } from './RidesDownloadSection';
import { RidesTransferSection } from './RidesTransferSection';
import { fetchNetworks, type UATNetwork } from '../../../services/fixedRouteUatClient';
import {
  ENVIRONMENT_OPTIONS,
  getEnvironmentDisplayName,
  getEnvironmentProductStorageKey,
  getEnvironmentTokenStorageKey,
  isProductionEnvironment,
} from '../../../services/environmentConfig';
import {
  frostedDrawerPaperSx,
  frostedDrawerHeaderSx,
  primaryPillButtonSx,
  secondaryPillButtonSx,
  drawerActionButtonSx,
} from '../../../theme/componentStyles';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import { opsColors } from '../../../theme/opsTokens';

interface DataTransferDrawerProps {
  open: boolean;
  onClose: () => void;
  initialDataType?: DataType; // Optional: Open with specific data type selected
}

export const DataTransferDrawer: React.FC<DataTransferDrawerProps> = ({
  open,
  onClose,
  initialDataType,
}) => {
  const { t } = useTranslation(['map', 'common']);
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment>('UAT');
  const [jwtToken, setJwtToken] = useState<string>(() => {
    return localStorage.getItem('atlas_uatJwtToken') || '';
  });
  const [selectedDataType, setSelectedDataType] = useState<DataType>(initialDataType || 'locations');
  const [showJwtDialog, setShowJwtDialog] = useState<boolean>(false);
  const [showJwtToken, setShowJwtToken] = useState<boolean>(false);
  const [tempJwtToken, setTempJwtToken] = useState<string>('');
  const [serviceDate, setServiceDate] = useState<string>(() => {
    // Default to fixed simulation date for global wall-time invariance.
    return DEFAULT_SIMULATION_DATE;
  });
  const [timezone, setTimezone] = useState<string>('Australia/Sydney');
  const [downloadAcknowledged, setDownloadAcknowledged] = useState<boolean>(() => {
    // Load from sessionStorage on mount
    const saved = sessionStorage.getItem('atlas_downloadAcknowledged');
    return saved === 'true';
  });
  
  // Network selection state (shared across all tabs)
  const [networks, setNetworks] = useState<UATNetwork[]>([]);
  const [isLoadingNetworks, setIsLoadingNetworks] = useState<boolean>(false);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedNetworkId, setSelectedNetworkId] = useState<number | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<UATNetwork | null>(null);

  // Reset selected data type when drawer closes
  useEffect(() => {
    if (!open) {
      // Reset selected data type to default
      setSelectedDataType('locations');
    } else if (open && initialDataType) {
      // When drawer opens with initialDataType, set it
      setSelectedDataType(initialDataType);
    }
  }, [open, initialDataType]);

  // Safety default: each drawer open starts in UAT.
  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedEnvironment('UAT');
    setNetworks([]);
    setSelectedProductId('');
    setSelectedNetworkId(null);
    setSelectedNetwork(null);
    setNetworkError(null);
  }, [open]);

  // Load JWT token from localStorage when environment changes
  useEffect(() => {
    const storageKey = getEnvironmentTokenStorageKey(selectedEnvironment);
    const savedToken = localStorage.getItem(storageKey) || '';
    if (jwtToken !== savedToken) {
      setJwtToken(savedToken);
    }
  }, [selectedEnvironment]);

  // Auto-load networks when JWT token is provided or environment changes
  useEffect(() => {
    if (jwtToken.trim() && !isLoadingNetworks && networks.length === 0 && !networkError) {
      const timer = setTimeout(() => {
        loadNetworks();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [jwtToken, selectedEnvironment, networks.length, isLoadingNetworks, networkError]);

  // Auto-update timezone when product selection changes
  useEffect(() => {
    if (selectedProductId && networks.length > 0) {
      const product = networks.find(p => p.productId === selectedProductId);
      if (product && product.timezone) {
        setTimezone(product.timezone);
      }
    }
  }, [selectedProductId, networks]);

  // Update selectedNetwork when selectedProductId changes
  useEffect(() => {
    if (selectedProductId && networks.length > 0) {
      const product = networks.find(p => p.productId === selectedProductId);
      if (product) {
        setSelectedNetwork(product);
      }
    } else if (!selectedProductId) {
      setSelectedNetwork(null);
    }
  }, [selectedProductId, networks]);

  // Load persisted network selection
  useEffect(() => {
    if (networks.length > 0) {
      const storageKey = getEnvironmentProductStorageKey(selectedEnvironment);
      const savedProductId = localStorage.getItem(storageKey);
      if (savedProductId) {
        const savedProduct = networks.find(p => p.productId === savedProductId);
        if (savedProduct) {
          setSelectedProductId(savedProductId);
          setSelectedNetworkId(savedProduct.defaultNetworkId);
          setSelectedNetwork(savedProduct);
          if (savedProduct.timezone) {
            setTimezone(savedProduct.timezone);
          }
        }
      }
    }
  }, [networks, selectedEnvironment]);

  const loadNetworks = async () => {
    if (!jwtToken.trim()) {
      return;
    }

    setIsLoadingNetworks(true);
    setNetworkError(null);

    try {
      const response = await fetchNetworks(jwtToken, selectedEnvironment);
      const sorted = (response.data.entry ?? []).slice().sort((a: UATNetwork, b: UATNetwork) =>
        a.productName.localeCompare(b.productName, undefined, { sensitivity: 'base' }),
      );
      setNetworks(sorted);
    } catch (error) {
      setNetworkError(error instanceof Error ? error.message : t('dataTransfer.drawer.errorLoadNetworks'));
    } finally {
      setIsLoadingNetworks(false);
    }
  };

  const handleProductChange = (productId: string) => {
    setSelectedProductId(productId);
    const product = networks.find(p => p.productId === productId);
    if (product) {
      setSelectedNetworkId(product.defaultNetworkId);
      setSelectedNetwork(product);
      // Persist selection
      const storageKey = getEnvironmentProductStorageKey(selectedEnvironment);
      localStorage.setItem(storageKey, productId);
    }
  };

  const handleNetworkChange = (network: UATNetwork | null) => {
    setSelectedNetwork(network);
    if (network) {
      setSelectedProductId(network.productId);
      setSelectedNetworkId(network.defaultNetworkId);
      // Persist selection
      const storageKey = getEnvironmentProductStorageKey(selectedEnvironment);
      localStorage.setItem(storageKey, network.productId);
    } else {
      setSelectedProductId('');
      setSelectedNetworkId(null);
    }
  };

  const handleEnvironmentChange = (newEnvironment: Environment) => {
    // Save current selections before switching
    if (selectedProductId) {
      const currentStorageKey = getEnvironmentProductStorageKey(selectedEnvironment);
      localStorage.setItem(currentStorageKey, selectedProductId);
    }

    // Switch environment
    const nextToken = localStorage.getItem(getEnvironmentTokenStorageKey(newEnvironment)) || '';
    setJwtToken(nextToken);
    setSelectedEnvironment(newEnvironment);
    
    // Clear networks for the new environment (will reload with new JWT token)
    setNetworks([]);
    setSelectedProductId('');
    setSelectedNetworkId(null);
    setSelectedNetwork(null);
    setNetworkError(null);
    
    // Service date is preserved (shared across environments)
    // Networks will auto-reload when JWT token loads for new environment
  };

  // Validate JWT token format
  const isTokenValid = jwtToken.trim().length > 0 && jwtToken.trim().length >= 10;

  const handleOpenJwtDialog = () => {
    setTempJwtToken(jwtToken);
    setShowJwtDialog(true);
  };

  const handleSaveJwtToken = () => {
    const storageKey = getEnvironmentTokenStorageKey(selectedEnvironment);
    const trimmedToken = tempJwtToken.trim();
    if (trimmedToken) {
      localStorage.setItem(storageKey, trimmedToken);
    } else {
      localStorage.removeItem(storageKey);
    }
    setJwtToken(trimmedToken);
    setShowJwtDialog(false);
  };

  const handleCloseJwtDialog = () => {
    setTempJwtToken('');
    setShowJwtDialog(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        sx: (theme) => ({
          width: 'calc(75vw - 65px)',
          maxWidth: 'calc(100vw - 65px)',
          p: 2,
          pr: 2, // Standard padding on right
          height: 'calc(100vh - 80px)', // Adjust height to account for logo
          top: 80, // Position below logo
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          right: 65,
          ...frostedDrawerPaperSx(theme),
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          overflow: 'hidden',
          zIndex: 2201,
        }),
      }}
    > 
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        sx={(theme) => ({
          px: 2,
          py: 1,
          borderRadius: 2,
          ...frostedDrawerHeaderSx(theme, 0.22),
        })}
      >
        <Box display="flex" alignItems="center" gap={2} flex={1}>
          <Typography variant="h6" fontWeight={700}>
            {t('dataTransfer.drawer.title')}
          </Typography>
          {/* Environment Dropdown */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={selectedEnvironment}
              onChange={(e) => handleEnvironmentChange(e.target.value as Environment)}
              inputProps={{ 'data-testid': 'transfer.env.select' }}
              MenuProps={{ sx: { zIndex: 2305 } }}
              sx={(theme) => ({
                height: 32,
                fontWeight: 600,
                color: opsColors.neutral.ink,
                backgroundColor: opsColors.neutral.panel,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: opsColors.neutral.line,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: opsColors.neutral.lineStrong,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: opsColors.brand.primary,
                },
                '& .MuiSelect-icon': {
                  color: opsColors.neutral.inkSoft,
                },
              })}
            >
              {ENVIRONMENT_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={(theme) => ({
                    fontWeight: 500,
                    color: opsColors.neutral.ink,
                    '&.Mui-selected': {
                      backgroundColor: opsColors.neutral.panelAlt,
                      color: opsColors.neutral.ink,
                    },
                  })}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Service Date */}
          <TextField
            label={t('dataTransfer.drawer.serviceDate')}
            type="date"
            size="small"
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
            inputProps={{ 'data-testid': 'transfer.service-date.input' }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: 160 }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {/* Consent Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={downloadAcknowledged}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setDownloadAcknowledged(newValue);
                  // Persist to sessionStorage
                  sessionStorage.setItem('atlas_downloadAcknowledged', String(newValue));
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                {t('dataTransfer.drawer.responsibleAcknowledgement')}
              </Typography>
            }
          />

          {/* JWT Token Toggle Button */}
          <Tooltip title={jwtToken ? (isTokenValid ? t('dataTransfer.drawer.jwtConfigured') : t('dataTransfer.drawer.jwtInvalid')) : t('dataTransfer.drawer.configureJwt')}>
            <IconButton
              size="small"
              onClick={handleOpenJwtDialog}
              data-testid="transfer.jwt.icon-btn"
              sx={{
                border: '1px solid',
                borderColor: jwtToken ? (isTokenValid ? 'success.main' : 'warning.main') : 'divider',
                bgcolor: jwtToken ? (isTokenValid ? 'success.light' : 'warning.light') : 'transparent',
                '&:hover': {
                  bgcolor: jwtToken ? (isTokenValid ? 'success.light' : 'warning.light') : 'action.hover',
                },
              }}
            >
              {jwtToken ? (
                isTokenValid ? (
                  <Icon size={18} name="circle-check" style={{ color: '#2e7d32' }} />
                ) : (
                  <Icon size={18} name="circle-exclamation" style={{ color: '#ed6c02' }} />
                )
              ) : (
                <Icon size={18} d={ICON_FALLBACKS.VpnKey} />
              )}
            </IconButton>
          </Tooltip>

          <IconButton onClick={onClose} aria-label={t('dataTransfer.drawer.closeTransferAria')} size="small">
            <Icon size={20} name="xmark" />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {isProductionEnvironment(selectedEnvironment) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('dataTransfer.drawer.productionManualJwtNotice')}
        </Alert>
      )}

      <Box flex={1} sx={{ overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <>
          {/* Tabs and Network Selection Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', mb: 2, mr: 7 }}>
            <Box sx={{ flex: 1 }}>
              <DataTypeTabs
                selectedTab={selectedDataType}
                onTabChange={setSelectedDataType}
              />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 40, alignSelf: 'center' }} />
            {/* Network Selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                data-testid="transfer.network.autocomplete"
                size="small"
                options={[...networks].sort((a, b) => 
                  (a.productName || '').localeCompare(b.productName || '')
                )}
                getOptionLabel={(option) =>
                  `${option.productName} (${option.totalNetworks === 1 ? t('dataTransfer.drawer.networkCountSingular', { count: option.totalNetworks }) : t('dataTransfer.drawer.networkCountPlural', { count: option.totalNetworks })})`
                }
                value={selectedNetwork}
                onChange={(_, newValue) => handleNetworkChange(newValue)}
                disabled={isLoadingNetworks}
                slotProps={{ popper: { sx: { zIndex: 2305 } } }}
                sx={{ minWidth: 350 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('dataTransfer.drawer.productNetworkLabel')}
                    sx={{ 
                      '& .MuiInputBase-root': { 
                        height: 32 
                      } 
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: selectedNetwork?.networkType ? (
                        <>
                          <Chip 
                            label={selectedNetwork.networkType} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              bgcolor: selectedNetwork.networkType === 'Mixed' 
                                ? 'warning.light' 
                                : selectedNetwork.networkType === 'ODB'
                                ? 'info.light'
                                : 'primary.light',
                              color: 'primary.contrastText',
                              mr: 1
                            }} 
                          />
                          {params.InputProps.endAdornment}
                        </>
                      ) : params.InputProps.endAdornment,
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const networkType = option.networkType;
                  return (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography sx={{ flex: 1 }}>
                          {option.productName} ({option.totalNetworks === 1 ? t('dataTransfer.drawer.networkCountSingular', { count: option.totalNetworks }) : t('dataTransfer.drawer.networkCountPlural', { count: option.totalNetworks })})
                        </Typography>
                        {networkType && (
                          <Chip 
                            label={networkType} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              bgcolor: networkType === 'Mixed' 
                                ? 'warning.light' 
                                : networkType === 'ODB'
                                ? 'info.light'
                                : 'primary.light',
                              color: 'primary.contrastText'
                            }} 
                          />
                        )}
                      </Box>
                    </li>
                  );
                }}
                isOptionEqualToValue={(option, value) => option.productId === value.productId}
              />
              {jwtToken.trim() && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isLoadingNetworks ? <CircularProgress size={16} /> : <Icon name="arrows-rotate" />}
                  onClick={loadNetworks}
                  disabled={isLoadingNetworks}
                  sx={(theme) => ({
                    minWidth: 'auto',
                    px: 1.5,
                    height: 32,
                    whiteSpace: 'nowrap',
                    ...secondaryPillButtonSx(theme),
                  })}
                >
                  {networks.length === 0 ? t('dataTransfer.drawer.load') : t('dataTransfer.drawer.reload')}
                </Button>
              )}
            </Box>
          </Box>
          {selectedDataType === 'locations' && (
            <LocationsDownloadSection
              jwtToken={jwtToken}
              environment={selectedEnvironment}
              networks={networks}
              isLoadingNetworks={isLoadingNetworks}
              networkError={networkError}
              selectedProductId={selectedProductId}
              selectedNetworkId={selectedNetworkId}
              onProductChange={handleProductChange}
              onLoadNetworks={loadNetworks}
              serviceDate={serviceDate}
              onServiceDateChange={setServiceDate}
              timezone={timezone}
              onTimezoneChange={setTimezone}
              downloadAcknowledged={downloadAcknowledged}
              onDownloadAcknowledgedChange={setDownloadAcknowledged}
            />
          )}
          {selectedDataType === 'routes' && (
            <RoutesTransferSection
              jwtToken={jwtToken}
              environment={selectedEnvironment}
              serviceDate={serviceDate}
              timezone={timezone}
              onServiceDateChange={setServiceDate}
              onTimezoneChange={setTimezone}
              networks={networks}
              isLoadingNetworks={isLoadingNetworks}
              networkError={networkError}
              selectedProductId={selectedProductId}
              selectedNetworkId={selectedNetworkId}
              onProductChange={handleProductChange}
              onLoadNetworks={loadNetworks}
              downloadAcknowledged={downloadAcknowledged}
              onDownloadAcknowledgedChange={setDownloadAcknowledged}
            />
          )}
          {selectedDataType === 'fleets' && (
            <FleetsDownloadSection
              jwtToken={jwtToken}
              environment={selectedEnvironment}
              networks={networks}
              isLoadingNetworks={isLoadingNetworks}
              networkError={networkError}
              selectedProductId={selectedProductId}
              selectedNetworkId={selectedNetworkId}
              onProductChange={handleProductChange}
              onLoadNetworks={loadNetworks}
              serviceDate={serviceDate}
              timezone={timezone}
              onServiceDateChange={setServiceDate}
              onTimezoneChange={setTimezone}
              downloadAcknowledged={downloadAcknowledged}
              onDownloadAcknowledgedChange={setDownloadAcknowledged}
            />
          )}
          {selectedDataType === 'rides' && (
            <RidesTransferSection
              jwtToken={jwtToken}
              environment={selectedEnvironment}
              serviceDate={serviceDate}
              timezone={timezone}
              onServiceDateChange={setServiceDate}
              onTimezoneChange={setTimezone}
              networks={networks}
              isLoadingNetworks={isLoadingNetworks}
              networkError={networkError}
              selectedProductId={selectedProductId}
              selectedNetworkId={selectedNetworkId}
              onProductChange={handleProductChange}
              onLoadNetworks={loadNetworks}
              downloadAcknowledged={downloadAcknowledged}
              onDownloadAcknowledgedChange={setDownloadAcknowledged}
            />
          )}
          {selectedDataType === 'serviceInfo' && (
            <ServiceInformationDownloadSection
              jwtToken={jwtToken}
              environment={selectedEnvironment}
              serviceDate={serviceDate}
              timezone={timezone}
              onServiceDateChange={setServiceDate}
              onTimezoneChange={setTimezone}
              networks={networks}
              isLoadingNetworks={isLoadingNetworks}
              networkError={networkError}
              selectedProductId={selectedProductId}
              selectedNetworkId={selectedNetworkId}
              onProductChange={handleProductChange}
              onLoadNetworks={loadNetworks}
              downloadAcknowledged={downloadAcknowledged}
              onDownloadAcknowledgedChange={setDownloadAcknowledged}
            />
          )}
        </>
      </Box>

      {/* JWT Token Dialog */}
      <Dialog open={showJwtDialog} onClose={handleCloseJwtDialog} maxWidth="sm" fullWidth sx={{ zIndex: 2500 }} data-testid="transfer.jwt.dialog.root">
        <DialogTitle>
          {t('dataTransfer.drawer.jwtDialogTitle', { env: getEnvironmentDisplayName(selectedEnvironment) })}
        </DialogTitle>
        <DialogContent>
          {isProductionEnvironment(selectedEnvironment) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('dataTransfer.drawer.productionManualJwtNotice')}
            </Alert>
          )}
          <TextField
            fullWidth
            type={showJwtToken ? 'text' : 'password'}
            label={t('dataTransfer.drawer.jwtDialogTitle', { env: getEnvironmentDisplayName(selectedEnvironment) })}
            value={tempJwtToken}
            onChange={(e) => setTempJwtToken(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
            inputProps={{ 'data-testid': 'transfer.jwt.dialog.input' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('dataTransfer.environment.tokenVisibilityAria')}
                    onClick={() => setShowJwtToken(!showJwtToken)}
                    edge="end"
                    size="small"
                  >
                    {showJwtToken ? <Icon size={20} name="eye-slash" /> : <Icon size={20} name="eye" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {tempJwtToken.trim() && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
              {tempJwtToken.trim().length >= 10 ? (
                <>
                  <Icon size={16} name="circle-check" style={{ color: '#2e7d32'}} />
                  <Typography variant="caption" color="success.main">
                    {t('dataTransfer.environment.tokenValid')}
                  </Typography>
                </>
              ) : (
                <>
                  <Icon size={16} name="circle-exclamation" style={{ color: '#ed6c02'}} />
                  <Typography variant="caption" color="warning.main">
                    {t('dataTransfer.environment.tokenInvalid')}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseJwtDialog}
            sx={(theme) => ({
              ...secondaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleSaveJwtToken}
            variant="contained"
            data-testid="transfer.jwt.dialog.save-btn"
            sx={(theme) => ({
              ...primaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default DataTransferDrawer;
